import React, { useCallback, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { Accordion, Select, Tooltip } from '@cimpress/react-components';
import { getAllDocuments } from 'src/selectors/layers';
import type { RootState } from 'src/store';
import getSceneVariation from './selectors/getSceneVariation';
import EachSurfaceVariable, { SurfaceConfiguration } from './EachSurfaceVariable';
import { toSelectOption } from 'src/util/misc';
import { updateConfiguration, removeConfiguration, addConfiguration } from './slice';
import { DEFAULT, SURFACE_ACTIONS } from './constants';
import { getDefaultForAttributeValues, AttributesOption } from './common';
import ErrorIcon from 'src/components/icons/ErrorIcon';
import { Document } from '../documents/slice';

const SURFACE_CONFIG_KEY = 'surfaceConfigurations';
const ERROR_MSG = 'One or more surface variable configurations have errors. Please fix them.';

interface Props {
    surfaceConfigurations: SurfaceConfiguration[];
    addConfiguration: (payload: { key: string; default: SurfaceConfiguration }) => void;
    removeConfiguration: (payload: { key: string; index: number }) => void;
    updateConfiguration: (payload: { key: string; index: number; newConfiguration: SurfaceConfiguration }) => void;
    documents: Document[];
    attributesOptions: AttributesOption[];
}

const SurfaceVariables = (props: Props) => {
    const {
        surfaceConfigurations,
        addConfiguration,
        removeConfiguration,
        updateConfiguration,
        documents,
        attributesOptions,
    } = props;

    const onAttributeSelectionChange = (selected: { value: string }[] | null) => {
        const currentSeletions = surfaceConfigurations.map((config) => config.variableAttribute);
        const newSelections = selected?.map((select) => select.value) ?? [];

        // add new configurations for additional selected attributes
        newSelections.forEach((select) => {
            if (!currentSeletions.includes(select)) {
                const attributeSelected = attributesOptions.find((option) => option.name === select);
                const valueOptions = attributeSelected?.values ?? [];
                const defaultValues = getDefaultForAttributeValues<{ action: string; page?: number }>(valueOptions, {
                    action: DEFAULT,
                });

                // set true values to increment page by 1 and leave everything else as the default
                Object.keys(defaultValues).forEach((key) => {
                    switch (key.toLocaleLowerCase()) {
                        case '1':
                        case 'true':
                            defaultValues[key] = { action: SURFACE_ACTIONS.INCREMENT, page: 1 };
                            break;
                        default:
                            // do nothing as the values already set to default
                            break;
                    }
                });

                addConfiguration({
                    key: SURFACE_CONFIG_KEY,
                    default: { variableAttribute: select, documents: [], values: defaultValues },
                });
            }
        });

        // remove any deselected attributes from configuration list
        currentSeletions.forEach((select, index) => {
            if (!newSelections.includes(select)) {
                removeConfiguration({ key: SURFACE_CONFIG_KEY, index });
            }
        });
    };

    const options = useMemo(() => {
        // `toSelectOption`'s declared return type also covers its single-value (string input)
        // branch; this call always passes an array, so asserting the real array shape here
        // instead of the loose/ambiguous union.
        return toSelectOption(attributesOptions.map((option) => option.name)) as { label: string; value: string }[];
    }, [attributesOptions]);

    const hasErrors = useMemo(
        () => props.surfaceConfigurations.some((each) => !!each.errorMessage),
        [props.surfaceConfigurations],
    );

    const getErrorMessage = useCallback(
        (configuration: SurfaceConfiguration) => {
            const { variableAttribute, documents: selectedDocuments } = configuration;

            let isValidDoc = true;
            for (const docId of selectedDocuments ?? []) {
                if (!documents.some((doc) => doc.id === docId)) {
                    // found an invalid document
                    isValidDoc = false;
                    break;
                }
            }

            // Check if the attribute selected is still a valid option
            if (variableAttribute && !options.some((attr) => attr.value === variableAttribute)) {
                return 'Selected attribute is no longer a valid option or is in conflict with attribute(s) in link information section. Please remove this configuration or remove the attribute from link information section.';
            }

            // Check if the selected documents is still a valid option
            if (!isValidDoc) {
                return 'Selected document(s) is no longer a valid option. Please remove the invalid document(s).';
            }

            return '';
        },
        [options, documents],
    );

    useEffect(() => {
        surfaceConfigurations.forEach((config, index) => {
            const errorMessage = getErrorMessage(config);
            if (errorMessage !== config.errorMessage) {
                updateConfiguration({ key: SURFACE_CONFIG_KEY, index, newConfiguration: { ...config, errorMessage } });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, documents]);

    return (
        <Accordion
            title={
                <div className='layers-header' data-testid='surface-variables-title'>
                    <div className='layers-header__text'>
                        {hasErrors && (
                            <Tooltip direction='top' contents={ERROR_MSG}>
                                <ErrorIcon />
                            </Tooltip>
                        )}
                        Surface Variables
                    </div>
                </div>
            }
            defaultOpen={false}>
            <div className='accordion-body-wrapper'>
                <div data-testid='surface-variable-attribute-selector'>
                    <Select
                        options={options}
                        value={
                            surfaceConfigurations.length > 0
                                ? (toSelectOption(surfaceConfigurations.map((config) => config.variableAttribute)) as {
                                      label: string;
                                      value: string;
                                  }[])
                                : null
                        }
                        onChange={(chosenOption) => onAttributeSelectionChange(chosenOption)}
                        isClearable={false}
                        label='Select Attribute(s)'
                        isMulti={true}
                    />
                </div>

                {surfaceConfigurations.map((each, index) => (
                    <>
                        <EachSurfaceVariable
                            /* eslint-disable react/no-array-index-key */
                            key={index}
                            configuration={each}
                            updateConfiguration={(data) =>
                                updateConfiguration({ key: SURFACE_CONFIG_KEY, index, newConfiguration: data })
                            }
                            removeConfiguration={() => removeConfiguration({ key: SURFACE_CONFIG_KEY, index })}
                            documents={documents}
                            getErrorMessage={getErrorMessage}
                        />
                    </>
                ))}
            </div>
        </Accordion>
    );
};

function mapStateToProps(state: RootState) {
    const { surfaceConfigurations } = getSceneVariation(state);
    const documents = getAllDocuments(state);

    return {
        surfaceConfigurations,
        documents,
    };
}

export default connect(mapStateToProps, {
    updateConfiguration,
    removeConfiguration,
    addConfiguration,
})(SurfaceVariables);
